import { NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  Clock,
  FileText,
  MessageSquare,
  LogOut,
  Zap,
  Menu,
  X,
  Building,
  FileSignature,
  Briefcase
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const adminNavItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
  { icon: FileSignature, label: "Agreements", path: "/admin/agreements" },
  { icon: Users, label: "Team Members", path: "/admin/workers" },
  { icon: Clock, label: "Timesheets", path: "/admin/timesheets" },
  { icon: Users, label: "Requests", path: "/admin/requests" },
  { icon: Building, label: "Contractor Profile", path: "/admin/contractor-profile" },
  { icon: MessageSquare, label: "Messages", path: "/admin/messages" },
];

const workerNavItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/worker" },
  { icon: Briefcase, label: "My Jobs", path: "/worker/assignments" },
  { icon: Clock, label: "Calendar", path: "/worker/calendar" },
  { icon: FileText, label: "History", path: "/worker/timesheets" },
];

const contractorNavItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/contractor" },
  { icon: Building, label: "Company Profile", path: "/contractor/company-profile" },
  { icon: Users, label: "Requests", path: "/contractor/requests" },
  { icon: Users, label: "Team Members", path: "/contractor/workers" },
  { icon: Clock, label: "Timesheets", path: "/contractor/timesheets" },
  { icon: MessageSquare, label: "Messages", path: "/contractor/messages" },
];

export const Sidebar = ({ userType }) => {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  const navItems = 
    userType === "admin" ? adminNavItems :
    userType === "worker" ? workerNavItems :
    contractorNavItems;

  const roleLabel = 
    userType === "admin" ? "Administrator" :
    userType === "worker" ? "Team Member" :
    "Gavs Bemanning AB";

  const handleLogout = () => {
    logout();
    window.location.href = "/login";
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden fixed top-4 left-4 z-50 bg-white shadow-md"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="mobile-menu-toggle"
      >
        {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </Button>

      {/* Overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "sidebar fixed lg:static inset-y-0 left-0 z-40 w-64 flex flex-col transform transition-transform duration-300",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-2 text-white">
            <Zap className="w-8 h-8 text-[#F37021]" />
            <span className="text-2xl font-bold tracking-tight">AGGREKO</span>
          </div>
          <p className="text-white/60 text-sm mt-1">{roleLabel}</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === `/${userType}`}
              onClick={() => setIsOpen(false)}
              className={({ isActive }) =>
                cn(
                  "nav-item flex items-center gap-3 px-4 py-3 rounded-sm text-white/80 font-medium",
                  isActive && "active"
                )
              }
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-white/10">
          <div className="px-4 py-3 text-white/80 mb-2">
            <p className="font-semibold truncate">{user?.name}</p>
            <p className="text-sm text-white/50 truncate">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sign Out
          </Button>
        </div>
      </aside>
    </>
  );
};
